import { type FC, useMemo } from 'react';
import Header from '@/components/UI/Header';
import Loading from '@/components/Loading';
import { partitionFlagsByType, type FeatureFlagList, type FeatureFlagUpdate } from './FeatureFlagConfig.utils';
import { type FieldValues, useForm } from 'react-hook-form';
import { _notNil } from '@/littledash';
import { useDispatch } from 'react-redux';
import { NoDataMessage } from '@/components/NoDataCard';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { errorToast } from '@/helpers';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import FormActions from './FormActions';
import FlagSection from './FlagSection';

/**
 * Admin console page for managing feature flags and tenant configuration.
 * Splits flags into two collapsible sections by type, supports parent/child nesting.
 */
export const FeatureFlagConfig: FC = () => {
  const { response: featureFlagsResponse, loading: loadingFeatureFlags } = useApiHook({
    endpoint: 'GET /api/v1/team/feature-flags',
    invokeOnInit: true,
  });

  if (loadingFeatureFlags) {
    return <Loading txt="Loading configuration" />;
  }

  if (!featureFlagsResponse?.body) {
    return (
      <div className="pa4" data-test-component="FeatureFlagConfig" data-test-element="no-data">
        <NoDataMessage
          title="Unable to Load Configuration"
          text="There was a problem loading feature flag configuration. Please refresh the page or try again later."
        />
      </div>
    );
  }

  return <FeatureFlagConfigForm data={featureFlagsResponse.body} />;
};

interface FeatureFlagConfigFormProps {
  data: FeatureFlagList;
}

export const FeatureFlagConfigForm: FC<FeatureFlagConfigFormProps> = ({ data }) => {
  const dispatch = useDispatch();

  const defaultValues = useMemo(() => Object.fromEntries(data.map((flag) => [flag.name, flag.value])), [data]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { dirtyFields, isValid, isSubmitting },
  } = useForm({ defaultValues });

  const { invoke: patchFeatureFlags } = useApiHook({
    endpoint: 'PATCH /api/v1/team/feature-flags',
    invokeOnInit: false,
    options: { onError: { toast: false, throw: true, capture: false } },
  });

  const onSubmit = async (formData: FieldValues) => {
    try {
      const formValuesArr = Object.keys(dirtyFields).map((key) => ({
        name: key,
        value: formData[key],
      })) as FeatureFlagUpdate;
      const { body } = await patchFeatureFlags({ body: formValuesArr });
      if (_notNil(body)) {
        const features = Object.fromEntries(body.map(({ name, value }) => [name, value]));
        dispatch({ type: 'TEAM_SET_FEATURES', features });
      }
      window.location.reload();
    } catch (cause) {
      errorToast('Error updating feature flags, please try again.');
      ExceptionHandler.captureException(
        new InVivoError('Could not save feature flag configuration', { cause, slug: 'feature-flag-config' })
      );
    }
  };

  const { featureFlags, tenantConfig } = useMemo(() => partitionFlagsByType(data), [data]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-test-component="FeatureFlagConfig" data-test-element="form">
      <div className="pb3">
        <Header
          mainHeaderText="Configuration"
          asideComponent={() => (
            <FormActions
              pendingCount={Object.keys(dirtyFields).length}
              isValid={isValid}
              isSubmitting={isSubmitting}
              onDiscard={() => reset()}
              onSave={handleSubmit(onSubmit)}
            />
          )}
        />
        <p className="f6 dark-gray mt2">Manage feature flags and tenant configuration settings for this tenant.</p>
        <hr className="bt b--moon-gray bb-0 bl-0 br-0 mt3 mb0" />
        <div className="mt2 flex flex-column" style={{ gap: '1.5rem' }}>
          <FlagSection
            title="Feature Flags"
            description="Temporary flags for beta and limited availability features. Removed once a feature reaches general availability."
            flags={featureFlags}
            control={control}
          />
          <FlagSection
            title="Tenant Configuration"
            description="Persistent settings that control this tenant's behavior."
            flags={tenantConfig}
            control={control}
          />
        </div>
      </div>
    </form>
  );
};
